import { FREQUENCY_CAPPING_FIELDS, TIME_PERIOD_UNIT, type TimePeriodUnit } from '@openx/types/frequencyCapping';
import type { I18nTranslate } from '@openx/types/translations';
import { convertTimeframeToHours } from '@openx/utils/lib/frequencyCappingUtils';
import { array, number, object, string } from 'yup';
import { emptyStringToUndefined } from './emptyStringToNullable';
import {
  FREQUENCY_CAPPING_IMPRESSIONS_LIMIT_MAX,
  FREQUENCY_CAPPING_IMPRESSIONS_LIMIT_MAX_LABEL,
} from './FrequencyCapping/constants';

const INTEGER_PATTERN = /^\d+$/;

export type FrequencyCappingValidationOptions = {
  maxEntries?: number;
};

export const getFrequencyCappingValidationSchema = (
  t: I18nTranslate,
  { maxEntries }: FrequencyCappingValidationOptions = {}
) => {
  let schema = array().of(
    object().shape({
      [FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_UNIT]: string()
        .oneOf(Object.values(TIME_PERIOD_UNIT), t('Please select a valid time unit'))
        .required(t('Time unit is required')),
      [FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_VALUE]: number()
        .transform(emptyStringToUndefined)
        .typeError(t('Duration must be a number'))
        .positive(t('Duration must be greater than 0'))
        .integer(t('Duration must be a whole number'))
        .max(365, t('Duration cannot exceed 365'))
        .required(t('Duration is required')),
      [FREQUENCY_CAPPING_FIELDS.IMPRESSIONS_LIMIT]: string()
        .transform(value => (typeof value === 'string' ? value.trim() : value))
        .required(t('Impressions limit is required'))
        .test('is-integer', t('Please enter a valid number'), value => {
          if (value == null || value === '') return false;

          return INTEGER_PATTERN.test(value);
        })
        .test('min', t('Impressions limit must be greater than 0'), value => {
          if (value == null || !INTEGER_PATTERN.test(value)) return true;

          return BigInt(value) >= 1n;
        })
        .test(
          'max',
          t('Impressions limit must not be greater than {max}', {
            max: FREQUENCY_CAPPING_IMPRESSIONS_LIMIT_MAX_LABEL,
          }),
          value => {
            if (value == null || !INTEGER_PATTERN.test(value)) return true;

            return BigInt(value) <= FREQUENCY_CAPPING_IMPRESSIONS_LIMIT_MAX;
          }
        ),
    })
  );

  if (maxEntries != null) {
    schema = schema.max(maxEntries, t('Frequency capping can contain at most {max} entries', { max: maxEntries }));
  }

  return schema.test(
    'no-duplicate-timeframes',
    t(
      'Frequency capping contains duplicate timeframes. Please remove duplicate entries (e.g., 1 day equals 24 hours, 1 week equals 7 days or 168 hours).'
    ),
    frequencyCappings => {
      if (!frequencyCappings || frequencyCappings.length <= 1) {
        return true;
      }

      const timeframesInHours: number[] = [];

      for (const capping of frequencyCappings) {
        const timePeriodValue = capping?.[FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_VALUE];
        const timePeriodUnit = capping?.[FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_UNIT];

        if (timePeriodValue && timePeriodUnit) {
          const hoursEquivalent = convertTimeframeToHours(timePeriodValue, timePeriodUnit as TimePeriodUnit);

          if (timeframesInHours.includes(hoursEquivalent)) {
            return false;
          }

          timeframesInHours.push(hoursEquivalent);
        }
      }

      return true;
    }
  );
};
